import React, { useState, useEffect } from "react";
import "./App.css";
import styled from "styled-components";
import {
    MELOGY_BACKGROUND_XDARK_GREY,
} from "./style/colors";
import GlobalStyle from "./style/GlobalStyle";
import { ThemeProvider } from "@mui/material/styles";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import muiTheme from "./style/muiTheme";
import LeftSidebar from "./components/sidebar/LeftSidebar";
import Main from "./components/Main";
import { CreateSongProgressBarContext } from "./contexts/CreateSongProgressBarContext";
import CreateSongProgressBar from "./components/progressbar/CreateSongProgressBar";
import { StyledToastContainer } from "./style/StyledToastContainer";
import { 
    createAndFetchMidi, 
    updateInstrumentSetting,
    updateKeySetting 
} from "./util/AppUtil";
import { showToastInfo } from "./style/ShowToast";
import { majorSlow } from "./style/styleSettings/majorSlow";
import RotateWarning from "./components/warnings/RotateWarning";

const AppContainer = styled.div`
    height: calc(var(--vh, 1vh) * 100);
    background-color: ${MELOGY_BACKGROUND_XDARK_GREY};
    display: flex;
`;

function App() {
    const [leftSideBarIsOpen, setLeftSideBarIsOpen] = useState(false);
    const maxNumberOfSongs = 50;
    const numberOfSongsToRemoveToFreeUpSpaceAtOneTime = 10;
    const [styles, setStyles] = useState([
        majorSlow,
    ]);
    const [selectedStyle, setSelectedStyle] = useState(styles[0]);
    const [selectedSong, setSelectedSong] = useState(null);
    const [progress, setProgress] = useState(0);
    const [songs, setSongs] = useState([]);
    const [isPortrait, setIsPortrait] = useState(
        window.innerHeight >= window.innerWidth
    );
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
    const [selectedInstruments, setSelectedInstruments] = useState(
        selectedStyle ? selectedStyle.filter((item) => item.group === "Instruments") : []
    );
    let songCounter = 1;
    const MINIMUM_USABLE_HEIGHT = 575;

    useEffect(() => {
        if (!selectedStyle) return;
        const newInstruments = selectedStyle.filter(
            (item) => item.group === "Instruments"
        );
        setSelectedInstruments((prev) =>
            JSON.stringify(newInstruments) === JSON.stringify(prev)
                ? prev
                : newInstruments
        );
    }, [selectedStyle]);

    useEffect(() => {
        const updateOrientationAndHeight = () => {
            const newIsPortrait = window.innerHeight >= window.innerWidth;
            setIsPortrait(newIsPortrait);
            setViewportHeight(window.innerHeight);
            
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
        };
    
        updateOrientationAndHeight();
    
        window.addEventListener('resize', updateOrientationAndHeight);
        window.addEventListener('orientationchange', updateOrientationAndHeight);
    
        return () => {
            window.removeEventListener('resize', updateOrientationAndHeight);
            window.removeEventListener('orientationchange', updateOrientationAndHeight);
        };
    }, []);

    const handleCreateAndFetchMidi = () => {
        createAndFetchMidi(setProgress, setSong, styles[0]);
    };

    const setSong = (midi) => {
        if (!midi) return;
        let name = `Song ${songCounter}`;
        while (!isNameUnique(name)) {
            songCounter++;
            name = `Song ${songCounter}`;
        }
        const newSong = { name: name, midi: midi };
        setSelectedSong(newSong);
        notifySongLimit();
        if (songs.length >= maxNumberOfSongs) {
            showToastInfo(
                "Removed the ten oldest songs to make room for new ones.",
                5000
            );
            setSongs((prevSongs) => [
                ...prevSongs.slice(numberOfSongsToRemoveToFreeUpSpaceAtOneTime),
                newSong,
            ]);
        } else {
            setSongs((prevSongs) => [...prevSongs, newSong]);
        }
    };

    const notifySongLimit = () => {
        const notifySongLimit =
            songs.length ===
            maxNumberOfSongs - numberOfSongsToRemoveToFreeUpSpaceAtOneTime - 1;
        if (notifySongLimit) {
            showToastInfo(
                `When reaching ${maxNumberOfSongs} songs, the ${numberOfSongsToRemoveToFreeUpSpaceAtOneTime} oldest will be removed to free up space.`,
                5000
            );
        }
    };

    const isNameUnique = (nameToCheck) =>
        !songs.some((song) => song.name === nameToCheck);

    const setSongName = (newName) => {
        const updatedSelectedSong = { ...selectedSong, name: newName };
        setSelectedSong(updatedSelectedSong);
        const updatedSongs = songs.map((song) =>
            song.name === selectedSong.name ? updatedSelectedSong : song
        );
        setSongs(updatedSongs);
    };

    const createSongProgressValue = React.useMemo(
        () => ({
            progress,
            setProgress,
        }),
        [progress]
    );

    const toggleLeftSideBar = () => {
        setLeftSideBarIsOpen(!leftSideBarIsOpen);
    };

    const updateStyle = (settingName, newValue) => {
        const updatedStyle = 
            settingName === "key" ? updateKeySetting(selectedStyle, newValue) : 
            settingName.startsWith("track") ? updateInstrumentSetting(selectedStyle, settingName, newValue) : 
            settingName === "styleSettingsName" ? selectedStyle.map((setting) => {
                if (setting.name !== settingName) {
                    return setting;
                }
                return {
                    ...setting,
                    styleSettingsName: newValue,
                };
            }) :
            selectedStyle.map((setting) => {
                    if (setting.name !== settingName) return setting;
                    if (setting.inputType === "RANGE_SLIDER") {
                        return {
                            ...setting,
                            lowerValue: newValue[0],
                            higherValue: newValue[1],
                        };
                    }
                    return { ...setting, value: newValue };
                });
         
        setSelectedStyle(updatedStyle);
        setStyles((prevStyles) =>
            prevStyles.map((style) =>
                style[0]?.styleSettingsName === selectedStyle[0]?.styleSettingsName
                    ? [...updatedStyle]
                    : style
            )
        );
    };

    return (
        <>
            <GlobalStyle />
            <ThemeProvider theme={muiTheme}>
                <CreateSongProgressBarContext.Provider value={createSongProgressValue}>
                <CreateSongProgressBar progress={progress} />
                {viewportHeight < MINIMUM_USABLE_HEIGHT ? (
                    <RotateWarning 
                        isPortrait={isPortrait} 
                        minimumHeight={MINIMUM_USABLE_HEIGHT} 
                    />
                ) : (
                    <AppContainer>
                    <StyledToastContainer position={toast.POSITION.TOP_RIGHT} />
                    <LeftSidebar
                        songs={songs}
                        selectedSong={selectedSong}
                        setSelectedSong={setSelectedSong}
                        toggleLeftSideBar={toggleLeftSideBar}
                        isOpen={leftSideBarIsOpen}
                    />
                    <Main
                        songs={songs}
                        setSelectedSong={setSelectedSong}
                        selectedSong={selectedSong}
                        selectedInstruments={selectedInstruments}
                        setSongName={setSongName}
                        isNameUnique={isNameUnique}
                        createAndFetchMidi={handleCreateAndFetchMidi}
                        toggleLeftSideBar={toggleLeftSideBar}
                        styles={styles}
                        selectedStyle={selectedStyle}
                        updateStyle={updateStyle}
                        setSelectedStyle={setSelectedStyle}
                        isPortrait={isPortrait}
                    />
                    </AppContainer>
                )}
                </CreateSongProgressBarContext.Provider>
            </ThemeProvider>
        </>
    );
}

export default App;